import { useMemo, type CSSProperties, type ReactNode } from "react";
import {
  AUTHORING_BLOCK_TYPE_OPTIONS,
  COMPLETION_CHROME_TOKENS,
  countByStatus,
  getBlockSpecificTokens,
  getTokenMetadata,
  useTheme,
  type ThemeTokens,
  type TokenMetadata,
  type TokenStatus,
} from "@course-authoring/shared-types";
import { createDesignLibraryStyles, type DesignLibraryStyles } from "./designLibraryStyles";

interface SectionDef {
  id: string;
  label: string;
}

const SECTIONS: SectionDef[] = [
  { id: "overview", label: "Overview" },
  { id: "color", label: "Color" },
  { id: "typography", label: "Typography" },
  { id: "spacing", label: "Spacing & Layout" },
  { id: "radius", label: "Radius" },
  { id: "shadow-motion", label: "Shadow & Motion" },
  { id: "buttons", label: "Buttons" },
  { id: "form-controls", label: "Form Controls" },
  { id: "containers", label: "Containers & Surfaces" },
  { id: "content-blocks", label: "Content Blocks" },
  { id: "sidebar", label: "Sidebar" },
  { id: "chrome", label: "Editor Chrome" },
  { id: "client-banner", label: "Client Banner" },
  { id: "notes", label: "Notes & Open Questions" },
];

export function DesignLibraryPage() {
  const { tokens } = useTheme();
  const styles = useMemo(() => createDesignLibraryStyles(tokens), [tokens]);
  const statusCounts = useMemo(() => countByStatus(), []);

  return (
    <div style={styles.layout}>
      <nav style={styles.nav} aria-label="Design library sections">
        <div style={styles.navHeader}>Sections</div>
        <ul style={styles.navList}>
          {SECTIONS.map((section) => (
            <li key={section.id}>
              <a href={`#${section.id}`} style={styles.navLink}>
                {section.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <main style={styles.main}>
        <header style={styles.pageHeader}>
          <h1 style={styles.pageTitle}>Design Library</h1>
          <p style={styles.pageSubtitle}>
            Generated from <code>themeTokens.ts</code>. This page is the source of truth for
            tokens shipped in the codebase — if a token is not here, it does not exist in the app.
          </p>
        </header>

        <StatusBanner styles={styles} counts={statusCounts} />

        <Section id="overview" title="Overview" description="How to use this page and how token statuses are governed." styles={styles}>
          <OverviewBody styles={styles} />
        </Section>

        <Section id="color" title="Color" description="Brand, surfaces, text, borders, and applied component palettes." styles={styles}>
          <ColorSection styles={styles} tokens={tokens} />
        </Section>

        <Section id="typography" title="Typography" description="Font family, type ramp, weights, and line heights." styles={styles}>
          <TypographySection styles={styles} tokens={tokens} />
        </Section>

        <Section id="spacing" title="Spacing & Layout" description="Spacing scale and the 10-column content grid." styles={styles}>
          <SpacingSection styles={styles} tokens={tokens} />
        </Section>

        <Section id="radius" title="Radius" description="Corner radius scale and applied examples." styles={styles}>
          <RadiusSection styles={styles} tokens={tokens} />
        </Section>

        <Section id="shadow-motion" title="Shadow & Motion" description="Elevation tokens and transition durations/easings." styles={styles}>
          <ShadowMotionSection styles={styles} tokens={tokens} />
        </Section>

        <Section id="buttons" title="Buttons" description="Action button states." styles={styles}>
          <ButtonsSection styles={styles} tokens={tokens} />
        </Section>

        <Section id="form-controls" title="Form Controls" description="Inputs, selects, toggles, sliders." styles={styles}>
          <Stub styles={styles} message="Wired in a follow-up — needs real component rendering rather than direct token display." />
        </Section>

        <Section id="containers" title="Containers & Surfaces" description="Cards, panels, modals, dialogs." styles={styles}>
          <ContainersSection styles={styles} tokens={tokens} />
        </Section>

        <Section id="content-blocks" title="Content Blocks" description="Driven by the authoring block registry — new blocks appear here automatically. Each block lists only the tokens specific to it; common foundation tokens (typography, spacing, etc.) live in their own sections above." styles={styles}>
          <ContentBlocksSection styles={styles} tokens={tokens} />
        </Section>

        <Section id="sidebar" title="Sidebar" description="Authoring sidebar (chrome) tokens — outside the learner-facing scope." styles={styles}>
          <SidebarSection styles={styles} tokens={tokens} />
        </Section>

        <Section id="chrome" title="Editor Chrome" description="Editor focus rings, workflow badges, error states, transitions." styles={styles}>
          <ChromeSection styles={styles} tokens={tokens} />
        </Section>

        <Section id="client-banner" title="Client Banner" description="Banner shown when an admin is viewing the authoring tool as a specific client account. Tokens are proposed — ratify before shipping." styles={styles}>
          <ClientBannerSection styles={styles} tokens={tokens} />
        </Section>

        <Section id="notes" title="Notes & Open Questions" description="Known gaps and governance decisions." styles={styles}>
          <NotesSection styles={styles} />
        </Section>
      </main>
    </div>
  );
}

// ─── Layout primitives ───────────────────────────────────────────────────

function Section({
  id,
  title,
  description,
  styles,
  children,
}: {
  id: string;
  title: string;
  description: string;
  styles: DesignLibraryStyles;
  children: ReactNode;
}) {
  return (
    <section id={id} style={styles.section}>
      <header style={styles.sectionHeader}>
        <h2 style={styles.sectionTitle}>{title}</h2>
        <p style={styles.sectionDescription}>{description}</p>
      </header>
      {children}
    </section>
  );
}

function Subsection({
  title,
  styles,
  children,
}: {
  title: string;
  styles: DesignLibraryStyles;
  children: ReactNode;
}) {
  return (
    <>
      <h3 style={styles.subsectionTitle}>{title}</h3>
      {children}
    </>
  );
}

function Stub({ styles, message }: { styles: DesignLibraryStyles; message: string }) {
  return <div style={styles.stub}>{message}</div>;
}

function StatusPill({
  status,
  styles,
}: {
  status: TokenStatus;
  styles: DesignLibraryStyles;
}) {
  return <span style={styles.statusPill(status)}>{status.replace("-", " ")}</span>;
}

// ─── Status banner ───────────────────────────────────────────────────────

function StatusBanner({
  styles,
  counts,
}: {
  styles: DesignLibraryStyles;
  counts: Record<TokenStatus, number>;
}) {
  const active = counts.active ?? 0;
  const proposed = counts.proposed ?? 0;
  const deprecated = counts.deprecated ?? 0;
  const needsReview = counts["needs-review"] ?? 0;
  const needsAttention = proposed + deprecated + needsReview;
  return (
    <div style={styles.statusBanner}>
      <div style={{ fontWeight: 600 }}>
        Status overview {needsAttention > 0 && `· ${needsAttention} need attention`}
      </div>
      <div style={styles.statusBannerRow}>
        <StatusPill status="active" styles={styles} /> {active}
        <StatusPill status="proposed" styles={styles} /> {proposed}
        <StatusPill status="deprecated" styles={styles} /> {deprecated}
        <StatusPill status="needs-review" styles={styles} /> {needsReview}
      </div>
      <div style={{ fontSize: "13px", color: "inherit", opacity: 0.75 }}>
        <strong>Active</strong> = approved and in use. <strong>Proposed</strong> = added but not yet ratified —
        review and promote or remove. <strong>Deprecated</strong> = scheduled for removal — migrate consumers
        and delete. <strong>Needs review</strong> = no metadata declared yet — assign a status.
      </div>
    </div>
  );
}

// ─── Overview body ───────────────────────────────────────────────────────

function OverviewBody({ styles }: { styles: DesignLibraryStyles }) {
  return (
    <div style={styles.notesBlock}>
      <p style={{ marginTop: 0 }}>
        <strong>How to read this page.</strong> Every token shown here is imported live from
        <code> packages/shared-types/src/themeTokens.ts</code>. The status pill next to each token
        comes from <code>tokenMetadata.ts</code> and indicates whether the token is approved,
        pending, or marked for removal.
      </p>
      <p>
        <strong>How to add a token.</strong> Add the value to <code>themeTokens.ts</code> and a
        matching metadata entry to <code>tokenMetadata.ts</code> with status{" "}
        <code>proposed</code> until reviewed.
      </p>
      <p style={{ marginBottom: 0 }}>
        <strong>How to retire a token.</strong> Set status to <code>deprecated</code>, set
        <code> replacedBy</code> and <code>removeBy</code>, migrate consumers, then delete the
        token. The page surfaces deprecated tokens at the top so they don't linger.
      </p>
    </div>
  );
}

// ─── Color section ───────────────────────────────────────────────────────

interface ColorEntry {
  path: string;
  value: string;
}

function flattenColorGroup(prefix: string, group: Record<string, unknown>): ColorEntry[] {
  const entries: ColorEntry[] = [];
  for (const [key, value] of Object.entries(group)) {
    const path = `${prefix}.${key}`;
    if (typeof value === "string") {
      entries.push({ path, value });
    } else if (value && typeof value === "object") {
      entries.push(...flattenColorGroup(path, value as Record<string, unknown>));
    }
  }
  return entries;
}

function ColorSection({ styles, tokens }: { styles: DesignLibraryStyles; tokens: ThemeTokens }) {
  const groups: Array<{ title: string; entries: ColorEntry[] }> = [
    { title: "Brand", entries: flattenColorGroup("brand", tokens.brand) },
    { title: "Backgrounds", entries: flattenColorGroup("backgrounds", tokens.backgrounds) },
    { title: "Text", entries: flattenColorGroup("text", tokens.text) },
    { title: "Borders", entries: flattenColorGroup("borders", tokens.borders) },
    { title: "Buttons", entries: flattenColorGroup("buttons", tokens.buttons) },
    { title: "Choice states", entries: flattenColorGroup("choice", tokens.choice) },
    { title: "Semantic (success / error)", entries: flattenColorGroup("semantic", tokens.semantic) },
    { title: "Workplace Color Spectrum", entries: flattenColorGroup("wcs", tokens.wcs) },
    { title: "Bar chart", entries: flattenColorGroup("bar", tokens.bar) },
    { title: "Likert", entries: flattenColorGroup("likert", tokens.likert) },
    { title: "Badge", entries: flattenColorGroup("badge", tokens.badge).filter((e) => e.path !== "badge.size") },
    { title: "Completion", entries: flattenColorGroup("completion", tokens.completion) },
  ];

  return (
    <>
      {groups.map((group) => (
        <Subsection key={group.title} title={group.title} styles={styles}>
          <div style={styles.tokenGrid}>
            {group.entries.map((entry) => (
              <ColorCard key={entry.path} entry={entry} styles={styles} />
            ))}
          </div>
        </Subsection>
      ))}
    </>
  );
}

function ColorCard({ entry, styles }: { entry: ColorEntry; styles: DesignLibraryStyles }) {
  const meta = getTokenMetadata(entry.path);
  return (
    <div style={styles.tokenCard}>
      <div style={styles.swatch(entry.value)} />
      <div style={styles.tokenMetaRow}>
        <span style={styles.tokenPath}>{entry.path}</span>
        <StatusPill status={meta.status} styles={styles} />
      </div>
      <div style={styles.tokenValue}>{entry.value}</div>
      {meta.description && <div style={styles.tokenDescription}>{meta.description}</div>}
      <TokenMetaFooter meta={meta} styles={styles} />
    </div>
  );
}

function TokenMetaFooter({
  meta,
  styles,
}: {
  meta: TokenMetadata;
  styles: DesignLibraryStyles;
}) {
  const items: ReactNode[] = [];
  if (meta.figmaName) items.push(<span key="figma">Figma: {meta.figmaName}</span>);
  if (meta.themeable) items.push(<span key="themeable">Themeable</span>);
  if (meta.accessibilityNotes) items.push(<span key="a11y">{meta.accessibilityNotes}</span>);
  if (meta.replacedBy) items.push(<span key="replaced">Use instead: {meta.replacedBy}</span>);
  if (meta.removeBy) items.push(<span key="remove">Remove by: {meta.removeBy}</span>);
  if (meta.proposalNote) items.push(<span key="proposal">{meta.proposalNote}</span>);
  if (items.length === 0) return null;
  return (
    <div style={{ ...styles.tokenDescription, display: "flex", flexDirection: "column", gap: "2px" }}>
      {items}
    </div>
  );
}

// ─── Typography section ──────────────────────────────────────────────────

function TypographySection({
  styles,
  tokens,
}: {
  styles: DesignLibraryStyles;
  tokens: ThemeTokens;
}) {
  const ramp: Array<{ key: string; sample: string }> = [
    { key: "h1", sample: "Heading 1 — major page title" },
    { key: "h2", sample: "Heading 2 — section title" },
    { key: "h3", sample: "Heading 3" },
    { key: "h4", sample: "Heading 4" },
    { key: "body", sample: "Body — default paragraph copy goes here." },
    { key: "display", sample: "Display — hero text" },
    { key: "callout", sample: "Callout" },
    { key: "lessonTitle", sample: "Lesson title" },
    { key: "calloutSm", sample: "Callout small" },
    { key: "calloutMd", sample: "Callout medium" },
    { key: "calloutLg", sample: "Callout large" },
  ];

  return (
    <>
      <Subsection title="Font family" styles={styles}>
        <div style={styles.tokenCard}>
          <div style={{ ...styles.tokenPath }}>typography.fontFamily</div>
          <div style={styles.tokenValue}>{tokens.typography.fontFamily}</div>
          <div
            style={{
              fontFamily: tokens.typography.fontFamily,
              fontSize: tokens.typography.sizes.md,
              marginTop: tokens.spacing.sm,
            }}
          >
            The quick brown fox jumps over the lazy dog. 0123456789
          </div>
        </div>
      </Subsection>

      <Subsection title="Type ramp" styles={styles}>
        <div style={{ display: "flex", flexDirection: "column", gap: tokens.spacing.md }}>
          {ramp.map(({ key, sample }) => (
            <TypeSampleRow key={key} rampKey={key} sample={sample} styles={styles} tokens={tokens} />
          ))}
        </div>
      </Subsection>

      <Subsection title="Sizes scale" styles={styles}>
        <ScaleTable
          styles={styles}
          rows={Object.entries(tokens.typography.sizes).map(([k, v]) => ({
            path: `typography.sizes.${k}`,
            value: v,
          }))}
        />
      </Subsection>

      <Subsection title="Weights" styles={styles}>
        <div style={{ display: "flex", flexDirection: "column", gap: tokens.spacing.sm }}>
          {Object.entries(tokens.typography.weights).map(([key, weight]) => (
            <div key={key} style={styles.tokenCard}>
              <div style={styles.tokenMetaRow}>
                <span style={styles.tokenPath}>typography.weights.{key}</span>
                <StatusPill status={getTokenMetadata(`typography.weights.${key}`).status} styles={styles} />
              </div>
              <div style={styles.tokenValue}>{weight}</div>
              <div
                style={{
                  fontFamily: tokens.typography.fontFamily,
                  fontSize: tokens.typography.sizes.lg,
                  fontWeight: weight,
                }}
              >
                The quick brown fox
              </div>
            </div>
          ))}
        </div>
      </Subsection>

      <Subsection title="Line heights" styles={styles}>
        <ScaleTable
          styles={styles}
          rows={Object.entries(tokens.typography.lineHeights).map(([k, v]) => ({
            path: `typography.lineHeights.${k}`,
            value: String(v),
          }))}
        />
      </Subsection>
    </>
  );
}

function TypeSampleRow({
  rampKey,
  sample,
  styles,
  tokens,
}: {
  rampKey: string;
  sample: string;
  styles: DesignLibraryStyles;
  tokens: ThemeTokens;
}) {
  const scale = (tokens.typography as unknown as Record<string, { size: string; weight: number; lineHeight: string }>)[rampKey];
  if (!scale) return null;
  const meta = getTokenMetadata(`typography.${rampKey}`);
  const sampleStyle: CSSProperties = {
    fontFamily: tokens.typography.fontFamily,
    fontSize: scale.size,
    fontWeight: scale.weight,
    lineHeight: scale.lineHeight,
    margin: 0,
  };
  return (
    <div style={styles.tokenCard}>
      <div style={styles.tokenMetaRow}>
        <span style={styles.tokenPath}>typography.{rampKey}</span>
        <StatusPill status={meta.status} styles={styles} />
      </div>
      <div style={styles.tokenValue}>
        size {scale.size} · weight {scale.weight} · line-height {scale.lineHeight}
      </div>
      <p style={sampleStyle}>{sample}</p>
      {meta.description && <div style={styles.tokenDescription}>{meta.description}</div>}
    </div>
  );
}

function ScaleTable({
  styles,
  rows,
}: {
  styles: DesignLibraryStyles;
  rows: Array<{ path: string; value: string }>;
}) {
  return (
    <table style={styles.table}>
      <thead>
        <tr>
          <th style={styles.tableHeader}>Token</th>
          <th style={styles.tableHeader}>Value</th>
          <th style={styles.tableHeader}>Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const meta = getTokenMetadata(row.path);
          return (
            <tr key={row.path}>
              <td style={{ ...styles.tableCell, ...styles.tokenPath }}>{row.path}</td>
              <td style={{ ...styles.tableCell, ...styles.tokenValue }}>{row.value}</td>
              <td style={styles.tableCell}>
                <StatusPill status={meta.status} styles={styles} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ─── Content blocks section ──────────────────────────────────────────────

function ContentBlocksSection({
  styles,
  tokens,
}: {
  styles: DesignLibraryStyles;
  tokens: ThemeTokens;
}) {
  return (
    <>
      {AUTHORING_BLOCK_TYPE_OPTIONS.map((option) => {
        const blockTokens = getBlockSpecificTokens(option.type);
        return (
          <div key={option.type} style={styles.blockShowcase}>
            <div style={styles.blockShowcaseHeader}>
              <h3 style={{ ...styles.subsectionTitle, marginTop: 0, marginBottom: 0 }}>
                {option.chooserLabel}
              </h3>
              <code style={styles.tokenValue}>{option.type}</code>
            </div>
            <div style={styles.blockShowcaseBody}>
              Renderer: <code>packages/ui-blocks/src/{rendererFileName(option.type)}</code>
            </div>
            {blockTokens.length > 0 ? (
              <div style={styles.tokenGrid}>
                {blockTokens.map((path) => (
                  <BlockTokenCard key={path} path={path} tokens={tokens} styles={styles} />
                ))}
              </div>
            ) : (
              <div style={styles.blockShowcaseBody}>
                Uses common foundation tokens only — no block-specific tokens.
              </div>
            )}
          </div>
        );
      })}

      <div style={styles.blockShowcase}>
        <div style={styles.blockShowcaseHeader}>
          <h3 style={{ ...styles.subsectionTitle, marginTop: 0, marginBottom: 0 }}>
            Course / lesson completion chrome
          </h3>
          <code style={styles.tokenValue}>cross-block</code>
        </div>
        <div style={styles.blockShowcaseBody}>
          Tokens for completion banners, course-completion badges, and the sidebar
          completion accent. Used across <code>CourseNavigation</code>,{" "}
          <code>CourseCompleteBanner</code>, and <code>CourseCompletionView</code> rather than
          owned by a single content block.
        </div>
        <div style={styles.tokenGrid}>
          {COMPLETION_CHROME_TOKENS.map((path) => (
            <BlockTokenCard key={path} path={path} tokens={tokens} styles={styles} />
          ))}
        </div>
      </div>
    </>
  );
}

function BlockTokenCard({
  path,
  tokens,
  styles,
}: {
  path: string;
  tokens: ThemeTokens;
  styles: DesignLibraryStyles;
}) {
  const value = resolveTokenValue(tokens, path);
  if (value === undefined) return null;
  const meta = getTokenMetadata(path);
  const isColor = typeof value === "string" && /^#|^rgb/.test(value);
  return (
    <div style={styles.tokenCard}>
      {isColor && <div style={styles.swatch(value)} />}
      <div style={styles.tokenMetaRow}>
        <span style={styles.tokenPath}>{path}</span>
        <StatusPill status={meta.status} styles={styles} />
      </div>
      <div style={styles.tokenValue}>{String(value)}</div>
      {meta.description && <div style={styles.tokenDescription}>{meta.description}</div>}
    </div>
  );
}

function resolveTokenValue(tokens: ThemeTokens, path: string): string | number | undefined {
  const segments = path.split(".");
  let current: unknown = tokens;
  for (const segment of segments) {
    if (current && typeof current === "object" && segment in current) {
      current = (current as Record<string, unknown>)[segment];
    } else {
      return undefined;
    }
  }
  return typeof current === "string" || typeof current === "number" ? current : undefined;
}

function rendererFileName(blockType: string): string {
  const pascal = blockType
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");
  return `${pascal}Renderer.tsx`;
}

// ─── Spacing & Layout section ────────────────────────────────────────────

function SpacingSection({
  styles,
  tokens,
}: {
  styles: DesignLibraryStyles;
  tokens: ThemeTokens;
}) {
  const spacingEntries = Object.entries(tokens.spacing) as Array<[keyof ThemeTokens["spacing"], string]>;

  return (
    <>
      <Subsection title="Spacing scale" styles={styles}>
        <div style={{ display: "flex", flexDirection: "column", gap: tokens.spacing.sm }}>
          {spacingEntries.map(([key, value]) => {
            const path = `spacing.${key}`;
            const meta = getTokenMetadata(path);
            return (
              <div
                key={key}
                style={{
                  display: "grid",
                  gridTemplateColumns: "200px 80px 1fr auto",
                  gap: tokens.spacing.md,
                  alignItems: "center",
                  padding: tokens.spacing.sm,
                  borderBottom: `1px solid ${tokens.borders.default}`,
                }}
              >
                <span style={styles.tokenPath}>{path}</span>
                <span style={styles.tokenValue}>{value}</span>
                <div
                  style={{
                    height: "12px",
                    width: value,
                    backgroundColor: tokens.brand.accent,
                    borderRadius: tokens.radius.xs,
                  }}
                />
                <StatusPill status={meta.status} styles={styles} />
              </div>
            );
          })}
        </div>
        {(() => {
          const desc = getTokenMetadata("spacing.lgPlus").description;
          return desc ? (
            <div style={{ ...styles.tokenDescription, marginTop: tokens.spacing.sm }}>{desc}</div>
          ) : null;
        })()}
      </Subsection>

      <Subsection title="Layout" styles={styles}>
        <ScaleTable
          styles={styles}
          rows={[
            { path: "layout.contentMaxWidth", value: tokens.layout.contentMaxWidth },
            { path: "layout.mobileBreakpoint", value: `${tokens.layout.mobileBreakpoint}px` },
            { path: "layout.sectionPaddingY.desktop", value: tokens.layout.sectionPaddingY.desktop },
            { path: "layout.sectionPaddingY.mobile", value: tokens.layout.sectionPaddingY.mobile },
            { path: "layout.sectionPaddingX.desktop", value: tokens.layout.sectionPaddingX.desktop },
            { path: "layout.sectionPaddingX.mobile", value: tokens.layout.sectionPaddingX.mobile },
            { path: "layout.sidebar.width", value: tokens.layout.sidebar.width },
            { path: "layout.sidebar.minWidth", value: tokens.layout.sidebar.minWidth },
            { path: "layout.sidebar.maxWidth", value: tokens.layout.sidebar.maxWidth },
          ]}
        />
      </Subsection>

      <Subsection title="10-column content grid" styles={styles}>
        <div style={styles.notesBlock}>
          <p style={{ marginTop: 0 }}>
            Player and preview block layouts use a 10-column proportional grid inside{" "}
            <code>contentMaxWidth</code>. Each layout component declares its own{" "}
            <code>gridTemplateColumns</code> using <code>fr</code> values that sum to 10. On mobile, layouts
            collapse to a single-column flex stack.
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(10, 1fr)",
              gap: tokens.spacing.xs,
              marginTop: tokens.spacing.md,
            }}
          >
            {Array.from({ length: 10 }).map((_, i) => (
              <div
                key={i}
                style={{
                  height: "32px",
                  backgroundColor: tokens.backgrounds.surfaceAlt,
                  borderRadius: tokens.radius.xs,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: tokens.typography.sizes.xs,
                  color: tokens.text.muted,
                }}
              >
                {i + 1}
              </div>
            ))}
          </div>
        </div>
      </Subsection>
    </>
  );
}

// ─── Radius section ──────────────────────────────────────────────────────

function RadiusSection({
  styles,
  tokens,
}: {
  styles: DesignLibraryStyles;
  tokens: ThemeTokens;
}) {
  const radiusEntries = Object.entries(tokens.radius);
  return (
    <div style={styles.tokenGrid}>
      {radiusEntries.map(([key, value]) => {
        const path = `radius.${key}`;
        const meta = getTokenMetadata(path);
        return (
          <div key={key} style={styles.tokenCard}>
            <div
              style={{
                width: "100%",
                height: "80px",
                backgroundColor: tokens.brand.primary,
                borderRadius: value,
              }}
            />
            <div style={styles.tokenMetaRow}>
              <span style={styles.tokenPath}>{path}</span>
              <StatusPill status={meta.status} styles={styles} />
            </div>
            <div style={styles.tokenValue}>{value}</div>
            {meta.description && <div style={styles.tokenDescription}>{meta.description}</div>}
          </div>
        );
      })}
    </div>
  );
}

// ─── Shadow & Motion section ─────────────────────────────────────────────

function ShadowMotionSection({
  styles,
  tokens,
}: {
  styles: DesignLibraryStyles;
  tokens: ThemeTokens;
}) {
  return (
    <>
      <Subsection title="Shadow" styles={styles}>
        <div style={styles.tokenGrid}>
          {Object.entries(tokens.shadow).map(([key, value]) => {
            const path = `shadow.${key}`;
            const meta = getTokenMetadata(path);
            return (
              <div key={key} style={styles.tokenCard}>
                <div
                  style={{
                    height: "80px",
                    backgroundColor: tokens.backgrounds.card,
                    borderRadius: tokens.radius.md,
                    boxShadow: value,
                    margin: tokens.spacing.sm,
                  }}
                />
                <div style={styles.tokenMetaRow}>
                  <span style={styles.tokenPath}>{path}</span>
                  <StatusPill status={meta.status} styles={styles} />
                </div>
                <div style={styles.tokenValue}>{value}</div>
                {meta.description && <div style={styles.tokenDescription}>{meta.description}</div>}
              </div>
            );
          })}
        </div>
      </Subsection>

      <Subsection title="Motion" styles={styles}>
        <ScaleTable
          styles={styles}
          rows={[
            { path: "ui.transitionDefault", value: tokens.ui.transitionDefault },
            { path: "ui.transitionSlow", value: tokens.ui.transitionSlow },
          ]}
        />
      </Subsection>
    </>
  );
}

// ─── Buttons section ─────────────────────────────────────────────────────

function ButtonsSection({
  styles,
  tokens,
}: {
  styles: DesignLibraryStyles;
  tokens: ThemeTokens;
}) {
  const baseButton: CSSProperties = {
    fontFamily: tokens.typography.fontFamily,
    fontSize: tokens.typography.sizes.base,
    fontWeight: tokens.typography.weights.semibold,
    padding: `${tokens.spacing.sm} ${tokens.spacing.lg}`,
    borderRadius: tokens.radius.sm,
    border: "none",
    cursor: "pointer",
  };
  return (
    <>
      <Subsection title="Action button — applied" styles={styles}>
        <div style={{ display: "flex", gap: tokens.spacing.md, flexWrap: "wrap" }}>
          <button style={{ ...baseButton, backgroundColor: tokens.buttons.action.bg, color: tokens.text.onBrand }}>
            Idle
          </button>
          <button style={{ ...baseButton, backgroundColor: tokens.buttons.action.hover, color: tokens.text.onBrand }}>
            Hover
          </button>
          <button
            style={{
              ...baseButton,
              backgroundColor: tokens.buttons.action.disabledBg,
              color: tokens.buttons.action.disabledFg,
              cursor: "not-allowed",
            }}
            disabled
          >
            Disabled
          </button>
        </div>
      </Subsection>

      <Subsection title="Tokens" styles={styles}>
        <div style={styles.tokenGrid}>
          {(["bg", "hover", "disabledBg", "disabledFg"] as const).map((key) => {
            const value = tokens.buttons.action[key];
            const path = `buttons.action.${key}`;
            return <ColorCard key={path} entry={{ path, value }} styles={styles} />;
          })}
        </div>
      </Subsection>
    </>
  );
}

// ─── Containers & Surfaces section ───────────────────────────────────────

function ContainersSection({
  styles,
  tokens,
}: {
  styles: DesignLibraryStyles;
  tokens: ThemeTokens;
}) {
  const samples: Array<{
    label: string;
    background: string;
    backgroundPath: string;
    radiusKey: keyof ThemeTokens["radius"];
    shadowKey?: keyof ThemeTokens["shadow"];
  }> = [
    { label: "Default card", background: tokens.backgrounds.card, backgroundPath: "backgrounds.card", radiusKey: "md", shadowKey: "sm" },
    { label: "Alt card", background: tokens.backgrounds.cardAlt, backgroundPath: "backgrounds.cardAlt", radiusKey: "md" },
    { label: "Results panel", background: tokens.backgrounds.resultsPanel, backgroundPath: "backgrounds.resultsPanel", radiusKey: "lg", shadowKey: "md" },
    { label: "Surface", background: tokens.backgrounds.surface, backgroundPath: "backgrounds.surface", radiusKey: "md" },
    { label: "Modal / dialog", background: tokens.backgrounds.card, backgroundPath: "backgrounds.card", radiusKey: "lg", shadowKey: "xl" },
  ];

  return (
    <>
      <div style={styles.tokenGrid}>
        {samples.map((sample) => (
          <div key={sample.label} style={styles.tokenCard}>
            <div
              style={{
                height: "100px",
                backgroundColor: sample.background,
                borderRadius: tokens.radius[sample.radiusKey],
                boxShadow: sample.shadowKey ? tokens.shadow[sample.shadowKey] : "none",
                border: `1px solid ${tokens.borders.default}`,
              }}
            />
            <div style={styles.tokenPath}>{sample.label}</div>
            <div style={styles.tokenValue}>
              {sample.backgroundPath} · radius.{sample.radiusKey}
              {sample.shadowKey && ` · shadow.${sample.shadowKey}`}
            </div>
          </div>
        ))}
      </div>
      <div style={{ ...styles.tokenDescription, marginTop: tokens.spacing.md }}>
        See <a href="#color" style={{ color: tokens.text.link }}>Color → Backgrounds</a> for the full
        list of surface tokens, and <a href="#radius" style={{ color: tokens.text.link }}>Radius</a> /{" "}
        <a href="#shadow-motion" style={{ color: tokens.text.link }}>Shadow & Motion</a> for the
        primitives composed here.
      </div>
    </>
  );
}

// ─── Sidebar section ─────────────────────────────────────────────────────

function SidebarSection({
  styles,
  tokens,
}: {
  styles: DesignLibraryStyles;
  tokens: ThemeTokens;
}) {
  const nav = tokens.navigation;
  return (
    <>
      <Subsection title="Applied preview" styles={styles}>
        <div
          style={{
            backgroundColor: tokens.backgrounds.sidebar,
            borderRadius: tokens.radius.md,
            padding: nav.titlePadding,
            maxWidth: "360px",
            border: `1px solid ${nav.borderColor}`,
          }}
        >
          <div
            style={{
              fontFamily: tokens.typography.fontFamily,
              fontSize: nav.titleFontSize,
              fontWeight: tokens.typography.weights.semibold,
              color: nav.titleColor,
              marginBottom: tokens.spacing.sm,
            }}
          >
            Lessons
          </div>
          <div style={{ padding: nav.listPadding, display: "flex", flexDirection: "column", gap: nav.itemGap }}>
            {[
              { label: "Introduction", active: true },
              { label: "Workplace harassment", active: false },
              { label: "Reporting (locked)", active: false, locked: true },
            ].map((item) => (
              <div
                key={item.label}
                style={{
                  padding: nav.itemPadding,
                  borderRadius: nav.itemBorderRadius,
                  backgroundColor: item.active ? nav.itemActiveBackground : "transparent",
                  color: item.active ? nav.itemActiveColor : nav.itemColor,
                  fontSize: nav.itemFontSize,
                  fontFamily: tokens.typography.fontFamily,
                  opacity: item.locked ? nav.itemLockedOpacity : 1,
                }}
              >
                {item.label}
              </div>
            ))}
          </div>
        </div>
      </Subsection>

      <Subsection title="Tokens" styles={styles}>
        <ScaleTable
          styles={styles}
          rows={Object.entries(nav).map(([key, value]) => ({
            path: `navigation.${key}`,
            value: typeof value === "number" ? String(value) : value,
          }))}
        />
      </Subsection>

      <Subsection title="Layout" styles={styles}>
        <ScaleTable
          styles={styles}
          rows={[
            { path: "layout.sidebar.width", value: tokens.layout.sidebar.width },
            { path: "layout.sidebar.minWidth", value: tokens.layout.sidebar.minWidth },
            { path: "layout.sidebar.maxWidth", value: tokens.layout.sidebar.maxWidth },
            { path: "backgrounds.sidebar", value: tokens.backgrounds.sidebar },
            { path: "borders.inverse", value: tokens.borders.inverse },
          ]}
        />
      </Subsection>
    </>
  );
}

// ─── Editor Chrome section ───────────────────────────────────────────────

function ChromeSection({
  styles,
  tokens,
}: {
  styles: DesignLibraryStyles;
  tokens: ThemeTokens;
}) {
  const workflowEntries: Array<[string, string]> = [
    ["Draft", tokens.ui.workflowDraft],
    ["Review", tokens.ui.workflowReview],
    ["Approved", tokens.ui.workflowApproved],
    ["Published", tokens.ui.workflowPublished],
    ["Archived", tokens.ui.workflowArchived],
  ];
  return (
    <>
      <Subsection title="Workflow badges" styles={styles}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: tokens.spacing.sm }}>
          {workflowEntries.map(([label, color]) => (
            <div
              key={label}
              style={{
                padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
                borderRadius: tokens.radius.full,
                backgroundColor: color,
                color: tokens.text.onBrand,
                fontSize: tokens.typography.sizes.xs,
                fontWeight: tokens.typography.weights.semibold,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                fontFamily: tokens.typography.fontFamily,
              }}
            >
              {label}
            </div>
          ))}
        </div>
      </Subsection>

      <Subsection title="Selection & focus" styles={styles}>
        <div style={{ display: "flex", gap: tokens.spacing.md, flexWrap: "wrap" }}>
          <div
            style={{
              padding: tokens.spacing.lg,
              border: `2px solid ${tokens.ui.selectionBorder}`,
              borderRadius: tokens.radius.md,
              fontFamily: tokens.typography.fontFamily,
              fontSize: tokens.typography.sizes.sm,
            }}
          >
            ui.selectionBorder
          </div>
          <div
            style={{
              padding: tokens.spacing.lg,
              outline: `3px solid ${tokens.ui.focusRing}`,
              outlineOffset: "2px",
              backgroundColor: tokens.ui.focusRingOffset,
              borderRadius: tokens.radius.md,
              fontFamily: tokens.typography.fontFamily,
              fontSize: tokens.typography.sizes.sm,
            }}
          >
            ui.focusRing
          </div>
        </div>
      </Subsection>

      <Subsection title="Error state" styles={styles}>
        <div
          style={{
            padding: tokens.spacing.md,
            backgroundColor: tokens.ui.errorBackground,
            border: `1px solid ${tokens.ui.errorBorder}`,
            color: tokens.ui.errorText,
            borderRadius: tokens.radius.sm,
            fontFamily: tokens.typography.fontFamily,
            fontSize: tokens.typography.sizes.sm,
            maxWidth: "480px",
          }}
        >
          Form validation messages use ui.errorBorder / ui.errorBackground / ui.errorText.
        </div>
      </Subsection>

      <Subsection title="Overlay marker" styles={styles}>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: tokens.spacing.xs,
            padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
            backgroundColor: tokens.ui.overlayMarkerBackground,
            border: `1px solid ${tokens.ui.overlayMarker}`,
            color: tokens.ui.overlayMarker,
            borderRadius: tokens.radius.sm,
            fontFamily: tokens.typography.fontFamily,
            fontSize: tokens.typography.sizes.xs,
            fontWeight: tokens.typography.weights.semibold,
          }}
        >
          Customized for this company
        </div>
      </Subsection>

      <Subsection title="Tokens" styles={styles}>
        <ScaleTable
          styles={styles}
          rows={Object.entries(tokens.ui).map(([key, value]) => ({
            path: `ui.${key}`,
            value,
          }))}
        />
      </Subsection>
    </>
  );
}

// ─── Client Banner section ───────────────────────────────────────────────

function ClientBannerSection({
  styles,
  tokens,
}: {
  styles: DesignLibraryStyles;
  tokens: ThemeTokens;
}) {
  return (
    <>
      <Subsection title="Preview" styles={styles}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: tokens.spacing.sm,
            padding: `${tokens.spacing.sm} ${tokens.spacing.xl}`,
            backgroundColor: tokens.banner.info.background,
            borderTop: `1px solid ${tokens.banner.info.border}`,
            borderBottom: `1px solid ${tokens.banner.info.border}`,
            color: tokens.text.primary,
            fontSize: "14px",
            lineHeight: 1.4,
          }}
          role="status"
        >
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
            <rect x="3" y="2" width="14" height="16" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
            <rect x="7.5" y="14" width="5" height="4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
            <circle cx="7" cy="7" r="1" fill="currentColor" />
            <circle cx="10" cy="7" r="1" fill="currentColor" />
            <circle cx="13" cy="7" r="1" fill="currentColor" />
            <circle cx="7" cy="10.5" r="1" fill="currentColor" />
            <circle cx="10" cy="10.5" r="1" fill="currentColor" />
            <circle cx="13" cy="10.5" r="1" fill="currentColor" />
          </svg>
          <span>
            <strong>Client View: Acme Corp</strong>
            {' '}Showing lessons assigned to this account.
          </span>
        </div>
      </Subsection>

      <Subsection title="Tokens" styles={styles}>
        <ScaleTable
          styles={styles}
          rows={[
            { path: "banner.info.background", value: tokens.banner.info.background },
            { path: "banner.info.border",     value: tokens.banner.info.border },
          ]}
        />
      </Subsection>
    </>
  );
}

// ─── Notes section ──────────────────────────────────────────────────────

function NotesSection({ styles }: { styles: DesignLibraryStyles }) {
  return (
    <div style={styles.notesBlock}>
      <p style={{ marginTop: 0 }}>
        <strong>Figma sync workflow is live.</strong> Paste a Figma design URL in a Claude
        conversation and the <code>figma-sync</code> skill auto-runs — it pulls the design,
        compares every styled value against the tokens on this page, snaps near-matches,
        proposes new tokens for repeated unmatched values, and flags single-use anomalies.
        Reports land in <code>docs/figma-sync/</code>; new tokens appear here under{" "}
        <em>proposed</em> until ratified.
      </p>
      <p>
        <strong>Open governance question:</strong> <code>borders.inverse</code> is flagged{" "}
        <em>needs-review</em> — its existing comment says it's outside the learner-facing
        design-system scope. Decide whether to keep it in the main token set or move it to a
        chrome-specific namespace.
      </p>
      <p style={{ marginBottom: 0 }}>
        <strong>Form Controls section</strong> remains stubbed because it requires real component
        rendering (input/select/toggle) rather than direct token display. Wire it when an input
        primitive lands in <code>ui-blocks</code>.
      </p>
    </div>
  );
}
